<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" id="calibre_pb_0">
<h2 class="nobreak" id="pgepubid00001"> <i class="calibre7">Chapter One</i></h2>
</div>
<p class="calibre10"><span class="smcap">Most</span> of us, at Cross Loman, have begun to forget
about Mrs. Harter and Captain Patch, and those of
us who still remember—and after all, it was only last
summer—hardly ever speak their names.</p>
<p class="calibre10">I know that Mary Ambrey remembers, just as I do.
Sometimes we talk about it to each other, and exchange
impressions and conjectures. Conjectures more
than anything, because neither of us has the inside
knowledge that alone could help one to a real understanding
of what happened. Mary goes by intuition
a good deal, and after all she did see something of
Mrs. Harter. Personally, I know less than anybody.
Bill Patch was my junior by many years and, though
I saw him very often, we were never anything more
than acquaintances. And Diamond Harter, oddly
enough, I scarcely spoke to at all. And yet I have so
vivid an impression of her strange personality that I<span id="Page_2" title="[2]"></span>
feel as though I understood her better than anyone
now living can ever do.</p>
<p class="calibre10">It is partly to rid myself of the obsession that she
is to me that I have set myself to reconstruct the
affair of last summer. It is said that antiquarians can
reconstruct an entire monster from a single bone. Perhaps,
as an amateur psychologist, I can reconstruct a
singularly enigmatic personality from—well, more than
a single fact, perhaps, but not much more. Impressions,
especially other people’s impressions, are not
facts. Besides, the most curious thing of all, to my
mind, is that they all saw her quite differently. The
aspect that she wore to Mary Ambrey, for instance,
was not that in which Claire, my wife, saw her.</p>
<p class="calibre10">And yet Claire—about whom I intend to write with
perfect frankness—is not devoid of insight, although
she exaggerates everything.</p>
<p class="calibre10">Claire lives upon the edge of a volcano.</p>
<p class="calibre10">This is her own metaphor, and certainly represents
quite accurately the state of emotional jeopardy in
which her days are passed—indeed, it would be truer
still to say that she lives upon the edge of a hundred
volcanoes, so that there can never be a complete
absence of eruptions.</p>
<p class="calibre10">She has really undergone a certain amount of suffering<span id="Page_3" title="[3]"></span>
in her life, and is, I think, all but entirely unaware
that most of it was avoidable.</p>
<p class="calibre10">Her powers of imagination, although in the old days
they helped to constitute her charm, are, and always
were, in excess of her self-control, her reason, and her
education. There are few combinations less calculated
to promote contentment in the possessors of them.</p>
<p class="calibre10">She is really incapable now of concentrating upon
any but a personal issue. Yet she expresses her opinion,
with passionate emphasis, upon a number of
points.</p>
<p class="calibre10">“An atheist,” says Claire, frequently, “is a fool.
Now an agnostic is not a fool. An agnostic says,
humbly, ‘<i class="calibre5">I don’t know</i>.’ But an atheist, who denies
the existence of a God, is a fool.”</p>
<p class="calibre10">It is perhaps needless to add that Claire considers
herself an agnostic.</p>
<p class="calibre10">She generally speaks in capital letters.</p>
<p class="calibre10">When she dislikes the course of action, as reported
in the <i class="calibre5">Times</i>, taken by any politician—and she has a
virulent and mutually inconsistent set of dislikes—Claire
is apt to remark vivaciously:</p>
<p class="calibre10">“All I can say is that So-and-so ought to be taken
out and <span class="allsmcap">HUNG</span>. Then he wouldn’t talk so much nonsense.”</p>
<p class="calibre10">Claire is, of course, an anti-prohibitionist because<span id="Page_4" title="[4]"></span>
“just look at America—it’s a perfect farce”—and an
anti-feminist because “women can exercise all the influence
they want to at home. I should like to see the
woman who can’t make her husband vote as she
wants him to vote!”</p>
<p class="calibre10">Socialism, in which Claire includes the whole of the
Labor Party, the Bolsheviks in Russia, and a large
number of entirely non-political organizations, she
condemns upon the grounds that “it is nonsense to
pretend that things could ever be equal. Place everyone
upon the same footing in every respect, and in a
week some people would have everything and others
nothing.”</p>
<p class="calibre10">Upon the question of birth control, so freely discussed
by our younger relatives, her views might be
epitomized (though not by herself, since Claire never
epitomizes anything, least of all views of her own).</p>
<p class="calibre10">The whole subject is disgusting. All those who
write or speak of it are actuated by motives of indecency,
and all those who read their writings or listen
to their speeches do so from unhealthy curiosity. God
Himself has definitely pronounced against any and
every form of birth control.</p>
<p class="calibre10">Of this last, Claire seems to be especially positive,
but I have never been able to find out from her exactly<span id="Page_5" title="[5]"></span>
where this revelation of the Almighty’s attitude of
mind is to be found.</p>
<p class="calibre10">It need scarcely be added that, to Claire, all pacifists
are unpatriotic and cowardly, all vegetarians cranks,
and all spiritualists either humbugs or hysterical
women.</p>
<p class="calibre10">Sometimes, but not often, she and I discuss these
things. But when I object to sweeping generalities,
Claire, unfortunately, feels that I am being something
which she labels as “always against” her, and she
then not infrequently bursts into tears.</p>
<p class="calibre10">Few of our discussions ever survive this stage.</p>
<p class="calibre10">It is very curious now to think that fifteen years ago
I was madly in love with Claire Ambrey. She refused
to marry me until I was smashed up in a flying experiment
in America.</p>
<p class="calibre10">Then she wrote and said that she loved me and had
always loved me and would marry me at once. I
suppose I believed this because at the moment I so
wanted to believe it, and because also, at the moment
she so intensely believed it herself.</p>
<p class="calibre10">The generosity and the self-deception were both
so like Claire. Her emotional impulses are so violent
and her capacity for sustained effort so small.</p>
<p class="calibre10">It would be ungracious, to say the least of it, to
dwell upon the failure that we both know our married<span id="Page_6" title="[6]"></span>
life to be. It is sufficient to say that, in tying herself
to a semi-cripple, with a too highly developed critical
faculty and a preference for facing facts stark and
undecorated, Claire, in a word—and a vulgar word
at that—bit off more than she could chew.</p>
<p class="calibre10">We have lived at Cross Loman Manor House ever
since my father’s death. The Ambreys, Claire’s
cousins, are our nearest neighbors, but they have been
at the Mill House only for the last seven years, and
Cross Loman looks upon them as newcomers. The
Kendals have been eighteen years at Dheera Dhoon,
which is the name unerringly bestowed by General
Kendal on their big stucco villa at the outskirts of
the town. Nancy Fazackerly was born at Loman Cottage,
lived there until she married, and came back
there, a few years afterward, widowed—and so on.
It is just the same with the tradespeople and the
farmers. Applebee was always the baker, and when
he died, Emma Applebee, his daughter, remained on
in the business. A boy, whom Emma Applebee has
always strenuously impressed upon us all as “my
little nephew,” will succeed Emma.</p>
<p class="calibre10">Halfway up Cross Loman Hill is the church, with
the rectory just below it. Bending has been there for
thirty years. Lady Annabel Bending, who was the
widow of a colonial governor when the Rector married<span id="Page_7" title="[7]"></span>
her, has been among us only for the last two years.</p>
<p class="calibre10">We all meet one another pretty frequently, but I
seldom care to take my wheel chair and my unsightly
crutch outside the park gates, and so my intercourse
is mostly with the people who come to the house.</p>
<p class="calibre10">Mary Ambrey and her children come oftenest.
Claire’s feelings, on the whole, are less often hurt by
Mary than by most other people. Claire neither likes,
admires, nor approves of Sallie and Martyn Ambrey,
but she is at the same time genuinely and pathetically
fond of them—a contradiction as painful to herself
as it is probably irksome to Martyn and Sallie.</p>
<p class="calibre10">Martyn has always been her favorite because he is
a boy. Throughout his babyhood she invariably spoke
of him as “little-Martyn-God-bless-his-dear-chubby-little-face,”
and she unconsciously resents it, now that
little Martyn has grown up and has ceased to be
chubby—which he did long before she ceased to call
him so. As for the formula of benediction, I think
Claire feels that God, in all probability, experiences
exactly the same difficulty as herself in viewing Sallie
and Martyn as real people at all.</p>
<p class="calibre10">On the whole, Martyn and Sallie do not behave well
toward Claire. They are cold and contemptuous, both
of them conscious of being logical, impersonal, and<span id="Page_8" title="[8]"></span>
supremely rational, where their cousin is none of these
things, but rather the exact contrary to them.</p>
<p class="calibre10">Martyn is twenty-one and at Oxford.</p>
<p class="calibre10">Sallie is a year younger, a medical student at London
University.</p>
<p class="calibre10">Neither of them has ever been heard to utter the
words “I’m sorry” after hurting anyone’s feelings.
Claire noted this long ago—but she has never realized
that it is simply because they are not sorry that they
omit the use of the time-honored formula.</p>
<p class="calibre10">They are both of them clever and both of them
good-looking. But I often find it strange that they
should be Mary Ambrey’s children.</p>
<p class="calibre10">She, too, is clever and good-looking, but in thinking
of her one substitutes other adjectives. Mary is
gifted, sensitive, intelligent, gracious, and beautiful,
and pre-eminently well bred.</p>
<p class="calibre10">The description reminds me of the game we called
“Sallie’s game” that she invented last summer. It
was that afternoon, incidentally, on which I first heard
Mrs. Harter’s name.</p>
<p class="calibre10">The Ambreys had come up to the Manor House
on the first day of the long vacation. There was the
slight constraint that is always perceptible when Claire
is present, unless she is being made the center of the<span id="Page_9" title="[9]"></span>
conversation. One felt the involuntary chafing of her
spirit.</p>
<p class="calibre10">After tea, she suddenly suggested that we should
play paper games.</p>
<p class="calibre10">“I’ve invented a new paper game,” Sallie said, joyously,
her eyes dancing. “It’s called Portraits, and
there are two ways of playing it. Either we each
write down five adjectives applicable to some person
we all know, and then guess whom it’s meant for, or
else we all agree on the same person and then write the
portraits and compare them.”</p>
<p class="calibre10">(“This,” thought I, “is the sort of game that ends in
at least one member of the party getting up and leaving
the room, permanently offended.”)</p>
<p class="calibre10">“Let’s try it,” said Claire, eagerly.</p>
<p class="calibre10">Personalities always appeal to her, until they are
directed against herself. But it is a part of her curious
pathos that she never really expects them to be directed
against herself. I looked at Mary Ambrey, and she
looked back at me with the faintest hint of resigned
amusement in her hazel eyes.</p>
<p class="calibre10">Just as Martyn had finished distributing pencils and
strips of paper the Misses Kendal were announced.</p>
<p class="calibre10">It was the twins, Dolly and Aileen.</p>
<p class="calibre10">They wear their hats on the backs of their heads,
and their skirts a little longer behind than in front,<span id="Page_10" title="[10]"></span>
as do all the Kendals, but they are nice-looking girls
in a bovine way. It is hard on them to compare them
with Sallie, who is ten years their junior, as slim and
as straight as a wand, and whose clothes invariably
produce a peculiarly dashing effect.</p>
<p class="calibre10">No Kendals are ever dashing.</p>
<p class="calibre10">“You’re just in time to learn a new game,” said
Martyn, proceeding to explain.</p>
<p class="calibre10">“We’re no good at this sort of thing,” said the
Kendals, with cheerful contempt for those who were.</p>
<p class="calibre10">“We shall be thoroughly out of it all, but we’ll try
and struggle along somehow.”</p>
<p class="calibre10">The Kendal reaction to life is a mixture of self-depreciation,
self-assertion, and a thorough-going, entirely
unvenomous pessimism in regard to past, present,
and future. There are four sisters, and one
brother, who is always spoken of by his family as
“poor old Ahlfred.”</p>
<p class="calibre10">Inquiries after Alfred, who is in business and comes
home only for week-ends, always elicit the assurance
that he is “struggling along somehow.”</p>
<p class="calibre10">General Kendal, known as Puppa, and Mrs. Kendal—Mumma—also
“struggle along somehow.”</p>
<p class="calibre10">When they were told about Sallie’s new game, Dolly
and Aileen Kendal looked horribly distrustful.</p>
<p class="calibre10">“How can one ever guess who it’s meant for, I<span id="Page_11" title="[11]"></span>
should like to know. It would be impossible,” said
Aileen.</p>
<p class="calibre10">“Would it?” Sallie remarked, dryly.</p>
<p class="calibre10">She caught her mother’s eye and relented.</p>
<p class="calibre10">“Of course, you can take a public character for your
portrait, if you like.”</p>
<p class="calibre10">“That would be much easier,” declared the Kendals
in a breath.</p>
<p class="calibre10">We all wrote on our pieces of paper, and bit the
ends of our pencils, and finally folded up the papers
and threw them into a bowl.</p>
<p class="calibre10">“Here goes,” said Dolly Kendal, recklessly.</p>
<p class="calibre10">“It’ll be all the same a hundred years hence,” Aileen
added, with her air of philosophical resignation.</p>
<p class="calibre10">The first slip read aloud by Martyn was my own.</p>
<p class="calibre10">“Kind-hearted, Indomitable, Pathetic, Unscrupulous,
Cheerful.”</p>
<p class="calibre10">“Nancy Fazackerly,” said Mary, instantly.</p>
<p class="calibre10">“But why indomitable?” I heard Dolly ask, in a
puzzled way.</p>
<p class="calibre10">“Excellent. Now here’s someone you’ll all guess,”
said Martyn, with a glance at his sister. “Rational,
Sympathetic, Intelligent, Reserved, Elusive.”</p>
<p class="calibre10">“Elusive is very good,” said Sallie.</p>
<p class="calibre10">“You’ve got it?” her brother asked.</p>
<p class="calibre10">“Of course.”</p>
<p class="calibre10"><span id="Page_12" title="[12]"></span>“Wait a minute,” said Claire. “Read it again.”</p>
<p class="calibre10">Martyn read it again, refraining from glancing at
his mother.</p>
<p class="calibre10">“Queen Mary,” Aileen Kendal suddenly suggested,
brightly.</p>
<p class="calibre10">Martyn considered her gravely.</p>
<p class="calibre10">“What makes you think it might be?” he inquired
at last, evidently honestly curious.</p>
<p class="calibre10">“Oh, I don’t know. You said we might take public
characters, and she was the first one I thought of.”</p>
<p class="calibre10">“It might be me, I suppose,” Claire said, thoughtfully,
“only it leaves out a good deal. I mean, I don’t
think those characteristics are the most salient ones.”</p>
<p class="calibre10">“Besides, some of them wouldn’t apply, Cousin
Claire,” said Sallie, ruthlessly. “For one thing, I
should never call you in the least—”</p>
<p class="calibre10">“Tell me who it is, Sallie,” her mother interrupted
her.</p>
<p class="calibre10">“You, of course. I guessed it directly and so did
Cousin Miles.”</p>
<p class="calibre10">“It’s good, I think,” said Martyn. “Elusive is the
very word I’ve been looking for to describe mother’s
sort of remoteness.”</p>
<p class="calibre10">I saw the Kendals exchange glances with one
another.</p>
<p class="calibre10">Certainly, it is quite inconceivable that in the family<span id="Page_13" title="[13]"></span>
circle at Dheera Dhoon Mumma should ever be thus
described, in her own presence, by her progeny.</p>
<p class="calibre10">“Read the next one,” said Claire, coldly.</p>
<p class="calibre10">The Kendals had each of them selected a member
of the royal family for analysis, and the adjectives that
they had chosen bore testimony rather to a nice sense
of loyalty than to either their powers of discernment
or any appreciation of the meaning of words.</p>
<p class="calibre10">Then came the catastrophe that Mary and I, at least,
had grimly foreseen from the start.</p>
<p class="calibre10">Sallie, of course, was responsible. She really has
very little sense of decency.</p>
<p class="calibre10">“Imaginative, Temperamental, Unbalanced, Egotistical,
Restless.”</p>
<p class="calibre10">There was a short, deathly silence.</p>
<p class="calibre10">“Did you mean it for Cousin Claire, Sallie?” said
Martyn, at last.</p>
<p class="calibre10">One felt it was something that he should even have
put it in the form of a question.</p>
<p class="calibre10">“Yes, but there’s something missing,” Sallie said,
bright and interested and detached. She and her contemporaries
dissect themselves freely, I believe, and
they are always bright and interested and detached.
“There were dozens of other things that I wanted to
put down, all just as descriptive.”</p>
<p class="calibre10">“My worst enemy could not call me egotistical,”<span id="Page_14" title="[14]"></span>
said Claire, in a trembling voice. “And it’s neither
true nor respectful, Sallie, to say such a thing. A game
is a game, but you show me that I’m foolish to allow
myself to take part in this sort of amusement with
you, as though I were of your own age. You take
advantage of it.”</p>
<p class="calibre10">“My mistake, Cousin Claire,” said Sallie, not at all
sorry, but evidently rather amused. “I just put what
I really thought. It didn’t occur to me that you’d
mind.”</p>
<p class="calibre10">“Of course I don’t ‘mind,’ my child.” Claire’s voice
had become a rapid staccato. “It makes me smile,
that’s all. What do you mean by calling me ‘unbalanced?’
I suppose there isn’t a woman of my age
anywhere to whom that word is less applicable.”</p>
<p class="calibre10">“Hadn’t we better play at something else?” said
Dolly Kendal. “I knew before we began that if anyone
put in real people it wouldn’t be a success. That
sort of thing always ends in somebody being offended.”</p>
<p class="calibre10">“There’s no question of being offended,” said Claire,
more offended than ever.</p>
<p class="calibre10">“Mumma always made the rule, when we were children
and used to play games like Consequences: present
company always excepted.”</p>
<p class="calibre10">“I should call that dull. But perhaps it was safe,”
Sallie conceded. “Shall we try the other game?<span id="Page_15" title="[15]"></span>
Choose a person, and then each do his or her portrait,
and compare them afterwards.”</p>
<p class="calibre10">The Kendals looked as though they did not think
this likely to be a very great improvement upon Sallie’s
last inspiration.</p>
<p class="calibre10">“Do me,” said Sallie, shamelessly.</p>
<p class="calibre10">“I think”—Mary’s gentle voice was unusually determined—“I
think we will adopt Mrs. Kendal’s rule
this time.”</p>
<p class="calibre10">“Then let’s do that Mrs. Harter, who goes to tea
with Mrs. Fazackerly. We all know her, don’t we?”</p>
<p class="calibre10">“Only very slightly.”</p>
<p class="calibre10">“All the more interesting.”</p>
<p class="calibre10">“She really has personality,” said Claire, who had
been silent, with compressed lips and a look of pain
in her big dark eyes. I think she felt that no one
was looking at her and so gave it up.</p>
<p class="calibre10">“But you’ve never seen Mrs. Harter, have you?”
Mary asked me.</p>
<p class="calibre10">“No, but carry on. Who is Mrs. Harter?”</p>
<p class="calibre10">“Old Ellison’s daughter. You remember Ellison,
the plumber?”</p>
<p class="calibre10">“Quite well. Is this the girl with the odd Christian
name?”</p>
<p class="calibre10">“Diamond—yes. She married young and went out
to the East about five years ago. I don’t think she’s<span id="Page_16" title="[16]"></span>
been to Cross Loman since. Now she’s here for a
year, I believe, having left the husband behind. The
children have met her with Nancy Fazackerly and
Martyn introduced her to me.”</p>
<p class="calibre10">“In the old days, of course, you’d have seen her
behind a typewriter in her father’s office?”</p>
<p class="calibre10">“Exactly.”</p>
<p class="calibre10">Mary smiled. The changes that the war has brought
about in social intercourse do not perturb her in the
least.</p>
<p class="calibre10">She can afford to accept them.</p>
<p class="calibre10">“Mother,” said Sallie, “have you finished Mrs. Harter?”</p>
<p class="calibre10">“One minute.”</p>
<p class="calibre10">The portraits, when they were read aloud, struck
me as forming rather an interesting comment upon
the person who had inspired them. Of the writers,
only the two Kendals were negligible as observers of
human nature.</p>
<p class="calibre10">“Bad-tempered, Determined, Intelligent, Pushing,
Handsome.”</p>
<p class="calibre10">That was Martyn’s version.</p>
<p class="calibre10">“Handsome!” ejaculated Sallie. Her own paper
began with the word “Repellent” and went on with
“Determined, Ambitious, Straightforward, Common.”</p>
<p class="calibre10">“I’ve got her down as ‘Common,’ too,” said Claire.<span id="Page_17" title="[17]"></span>
“Common, Self-willed, Good-looking, Obstinate, and
Hard.”</p>
<p class="calibre10">“What a pleasing aggregate!” said I. “Mary, what
do you make of Mrs. Harter?”</p>
<p class="calibre10">“Sincere, Unhappy, Reserved, Ill-tempered, Undisciplined.”</p>
<p class="calibre10">“It’s queer,” said Martyn. “We’ve all been impressed
by that woman more or less. And yet we’ve
all noticed different things about her.”</p>
<p class="calibre10">“Two people said she was common,” Sallie pointed
out.</p>
<p class="calibre10">“I don’t agree.”</p>
<p class="calibre10">“Well,” said Dolly Kendal, “it’s not a very nice thing
to say about anyone, is it?”</p>
<p class="calibre10">This comment did not materially add to the value
of the discussion and met with no rejoinder.</p>
<p class="calibre10">“Mrs. Harter is common,” said Claire, with that
air of finality with which she invests an assertion of
her own opinion, particularly when it is contrary to
that held by other people. “But she has personality.
That’s why we’re all discussing her, I suppose—old
Ellison’s daughter!”</p>
<p class="calibre10">“She doesn’t look like old Ellison’s daughter,” Martyn
observed, replying, perhaps, rather to the spirit
than to the letter of Claire’s assertion. “It was a<span id="Page_18" title="[18]"></span>
stroke of genius on his part to have christened his
daughter Diamond.”</p>
<p class="calibre10">Sallie looked intelligently inquiring.</p>
<p class="calibre10">“Don’t you see how it suits her? The mixture of
hardness and of depth, and the slight tinge of vulgarity
that one can’t help associating with that sort
of name—and, of course, the unusualness. By the
way, didn’t anyone put her down as unusual?”</p>
<p class="calibre10">Claire shook her head.</p>
<p class="calibre10">“She may be good-looking, but she’s as hard as
nails, I should say—and she’s common.”</p>
<p class="calibre10">I began to feel that I should be interested to meet
Mrs. Harter.</p>
<p class="calibre10">Ellison, the plumber in Cross Loman, was a decent
old fellow—he died a few months ago—a very ordinary
type of the tradesman class. His wife had been
dead many years and I knew nothing about her. I
could not remember anything about the daughter except
that I had always heard her spoken of by her
full name—Diamond Ellison—and that the singularity
of it had remained somewhere in the background of
my memory. “I should like to see her,” I said.</p>
<p class="calibre10">“You can see her if you go to the concert at the
Drill Hall on the fourteenth,” Aileen Kendal told me.
“She is singing.”</p>
<p class="calibre10">“She’s musical, is she?”</p>
<p class="calibre10"><span id="Page_19" title="[19]"></span>“I suppose so. Lady Annabel arranged it all.”</p>
<p class="calibre10">“Why is Lady Annabel having a concert at all?”</p>
<p class="calibre10">“Something to do with the Women’s Institute,” said
Dolly. “You know she is always doing things for
them, and she has quite worried Mumma about belonging,
or letting us belong.”</p>
<p class="calibre10">Mrs. Kendal still “lets” or does not “let” her daughters,
in the minor as well as in the major affairs of
life, although Blanche, the eldest, must be thirty-seven.</p>
<p class="calibre10">“Mumma always says, ‘Be not the first by whom
the new is tried, Nor yet the last to lay the old aside,’”
Aileen solemnly quoted. “She says that Women’s Institutes
are a new movement, and she wants to know
rather more about them before she gives them her
support.”</p>
<p class="calibre10">The Kendals are not naturally sententious, but when
they quote either Puppa or Mumma they become so
to an unbearable degree.</p>
<p class="calibre10">Claire, who is patient neither of sententiousness nor
of quotations from other people, changed the subject.</p>
<p class="calibre10">“I’ve taken tickets, Miles, of course. Shall you want
to come? It will only be the usual kind of Cross
Loman concert.”</p>
<p class="calibre10">“Everybody is going, as usual. Nancy Fazackerly
is taking her paying guest.”</p>
<p class="calibre10"><span id="Page_20" title="[20]"></span>“Has she got one?”</p>
<p class="calibre10">“Hadn’t you heard?” cried everybody except my
wife and Mary Ambrey.</p>
<p class="calibre10">“He is a man called Captain Patch—quite young—and
he is coming next week. Nancy Fazackerly told us
all about it after church on Sunday.”</p>
<p class="calibre10">“She is coming up here to-morrow, so we shall hear
about it,” said Claire.</p>
<p class="calibre10">“I shall go to the concert,” I said, decidedly, “if it’s
only for the sake of seeing Mrs. Harter and Captain
Patch.”</p>
<p class="calibre10">It occurs to me now, as I write, that perhaps that
was the first time we heard their names thus coupled
together—Mrs. Harter and Captain Patch.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section003_0001.vue?6aa3063a